import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AuthStatus, LocalStorageToken } from '@shared/models/auth/auth.model';
import { APP_VERSION } from '../../../environments/version';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    req = req.clone({
      setHeaders: { 'X-Client-Version': APP_VERSION }
    });

    // `/liveness/` faz parte do fluxo de assinatura de contrato por usuário externo
    // (cônjuge não logado). Os endpoints `api/liveness/*` não têm guard no backend,
    // então não exigem token. Sem essa exceção, a chamada de validação facial passa
    // pelo gate de auth abaixo, cai em ALL_TOKENS_INVALID e redireciona o usuário
    // externo para a página inicial — abortando a assinatura.
    // `/version/check` é público no server e roda no boot do app nativo — sem a
    // exceção, um boot com tokens expirados fazia a própria checagem de versão
    // disparar o redirect de login antes de qualquer interação do usuário.
    // Os endpoints de assinatura do Stripe abaixo também são públicos no server:
    // o visitante anônimo assina direto da página /precos (o webhook cria a
    // conta pelo e-mail do checkout). Sem a exceção, o gate ALL_TOKENS_INVALID
    // redirecionava para /inicio e engolia a request (modal de checkout vazio).
    // NÃO incluir create-credit-checkout aqui — esse exige token.
    const STRIPE_PUBLIC_CHECKOUT_PATHS = [
      '/integracoes/stripe/create-subscription-checkout',
      '/integracoes/stripe/subscription-checkout/',
      '/integracoes/stripe/verificar-elegibilidade-assinatura',
      '/integracoes/stripe/checkout-session/',
    ];
    // Leitura de anexos (ex.: preview de CSV via HttpClient no crd-file-viewer)
    // aponta direto pro bucket público do GCS, não pro nosso server. Anexar o
    // JWT interno nessa chamada cross-origin fazia o GCS responder 401 (ele
    // tenta autenticar com o header em vez de servir como leitura anônima),
    // e o 401 disparava o redirect de login no meio da visualização do arquivo.
    if (
      req.url.includes('/auth/') ||
      req.url.includes('public') ||
      req.url.includes('/liveness/') ||
      req.url.includes('/version/check') ||
      req.url.includes('storage.googleapis.com') ||
      STRIPE_PUBLIC_CHECKOUT_PATHS.some((path) => req.url.includes(path))
    ) {
      return next.handle(req);
    }

    const token: LocalStorageToken = this.authService.getToken();
    const authStatus: AuthStatus = this.authService.isAuthenticated(token);
    switch (authStatus) {
      case AuthStatus.ACCESS_TOKEN_VALID:
        req = req.clone({
          setHeaders: {
            Authorization: token.jwtToken
          }
        })
        return this.handleDefaultRequest(next, req);
      case AuthStatus.REFRESH_TOKEN_VALID:
        const isRefreshingToken = this.authService.isRefreshingToken$.getValue();
        if (isRefreshingToken) {
          return this.authService.isRefreshingToken$.pipe(
            filter(val => val === false),
            take(1),
            timeout(10000),
            switchMap(
              () => {
                const updatedToken = this.authService.getToken();
                req = req.clone({
                  setHeaders: {
                    Authorization: updatedToken.jwtToken
                  }
                })
                return this.handleDefaultRequest(next, req);
              }
            ),
            catchError(
              err => {
                this.authService.isRefreshingToken$.next(false);
                this.redirectLoginPage(false, err);
                return EMPTY;
              }
            )
          );
        } else {
          this.authService.isRefreshingToken$.next(true);
          return this.authService.refreshToken(token.refreshToken)
          .pipe(
            switchMap(
              res => {
                this.authService.updateTokens(res);
                this.authService.isRefreshingToken$.next(false);
                req = req.clone({
                  setHeaders: {
                    Authorization: res.newAccessToken
                  }
                })
                return this.handleDefaultRequest(next, req);
              }
            ),
            catchError(
              err => {
                this.authService.isRefreshingToken$.next(false);
                this.redirectLoginPage(false, err);
                return EMPTY;
              }
            )
          )
        }
      case AuthStatus.ALL_TOKENS_INVALID:
      default:
        this.redirectLoginPage(true);
        return EMPTY;
    }
  }

  private handleDefaultRequest(next: HttpHandler, req: HttpRequest<any>): Observable<any> {
    return next.handle(req).pipe(
      catchError(
        (err: HttpErrorResponse) => {
          if (err.status === 401) {
            // Server rejeitou o token em definitivo — limpa para o AuthGuard
            // não deixar passar de novo com credenciais mortas (loop de bounce).
            this.redirectLoginPage(true);
            return EMPTY;
          }
          return throwError(err);
        }
      )
    )
  }

  /**
   * Encaminha para a página inicial com o destino pendente em ?redirectUrl.
   *
   * - Não empilha: se o usuário já está em /inicio ou /auth/*, não navega —
   *   antes, cada 401 tardio capturava a URL atual e gerava redirectUrl
   *   aninhado (/inicio?redirectUrl=%2Finicio%3FredirectUrl...), mandando o
   *   usuário logado de volta ao marketing após o login.
   * - `sessionExpired=true` sinaliza aos guards de /inicio e /auth/login que
   *   este pouso veio de um bounce: eles não devem auto-redirecionar um
   *   usuário com tokens localmente "válidos" que o server rejeitou.
   * - `clearTokens` remove o storage quando a rejeição é definitiva (tokens
   *   expirados localmente ou 401/403 do server); falhas transitórias de
   *   rede no refresh mantêm os tokens para a próxima tentativa.
   */
  private redirectLoginPage(clearTokens = false, err?: unknown): void {
    const status = (err as HttpErrorResponse)?.status;
    if (clearTokens || status === 401 || status === 403) {
      this.authService.logout();
    }

    const currentUrl = this.router.routerState.snapshot.url.toString();
    if (currentUrl.startsWith('/inicio') || currentUrl.startsWith('/auth')) return;

    this.router.navigate(['inicio'], {
      queryParams: { redirectUrl: currentUrl, sessionExpired: true }
    });
  }
}
